/**
 * BasicDetails Widget
 * Displays consolidated birth information in a compact, readable format
 */

import React from 'react';
import { Box, Typography, Table } from '@mui/joy';
import { RASHI_NAMES, PLANET_ORDER } from '../../utils/astroEngine';
import InfoTooltip from '../InfoTooltip';

interface BasicDetailsProps {
  // Birth information
  name?: string;
  birthDateTime?: string; // local ISO string
  birthPlace?: string;
  
  // Coordinates
  latitude?: number;
  longitude?: number;
  timezone?: string;
  utcDateTime?: string;
  
  // Astrological data
  ascendant?: {
    signIndex: number;
    signName: string;
    longitudeDeg: number;
    nakshatra: string;
    pada: number;
  };
  
  moonSign?: {
    signIndex: number;
    signName: string;
    longitudeDeg: number;
    nakshatra: string;
    pada: number;
  };
  
  // Planetary positions (sidereal)
  grahaLongitudes?: Partial<Record<string, number>>;
  
  // House cusps (whole sign system)
  houses?: Record<number, { signIndex: number; signName: string }>;
  
  // Planets in houses
  planetsInHouses?: Record<string, number>; // planet name -> house number
}

export default function BasicDetails(props: BasicDetailsProps) {
  const {
    name,
    birthDateTime,
    birthPlace,
    latitude,
    longitude,
    timezone,
    utcDateTime,
    ascendant,
    moonSign,
    grahaLongitudes,
    houses,
    planetsInHouses,
  } = props;

  // Format coordinates
  const formatCoord = (val: number | undefined, isLat: boolean) => {
    if (val === undefined) return '—';
    const abs = Math.abs(val);
    const dir = isLat ? (val >= 0 ? 'N' : 'S') : (val >= 0 ? 'E' : 'W');
    return `${abs.toFixed(4)}° ${dir}`;
  };

  // Format longitude in sign
  const formatLongitude = (deg: number | undefined) => {
    if (deg === undefined) return '—';
    const signIdx = Math.floor(deg / 30);
    const signDeg = deg % 30;
    const signName = RASHI_NAMES[signIdx] || `Sign${signIdx + 1}`;
    return `${signDeg.toFixed(2)}° ${signName}`;
  };

  return (
    <Box sx={{ 
      fontSize: '0.875rem',
      lineHeight: 1.5,
    }}>
      {/* Compact Birth Information & Key Details */}
      <Table 
        size="sm" 
        sx={{ 
          '& td': { py: 0.4, fontSize: '0.875rem' },
          '& td:first-of-type': { fontWeight: 600, width: '140px', color: 'text.secondary' },
          mb: 2,
        }}
      >
        <tbody>
          {name && (
            <tr>
              <td>Name</td>
              <td>{name}</td>
            </tr>
          )}
          {birthDateTime && (
            <tr>
              <td>Birth Date</td>
              <td>{new Date(birthDateTime).toLocaleString('en-IN', { 
                dateStyle: 'medium', 
                timeStyle: 'short' 
              })}</td>
            </tr>
          )}
          {birthPlace && (
            <tr>
              <td>Birth Place</td>
              <td>{birthPlace}</td>
            </tr>
          )}
          <tr>
            <td>Coordinates</td>
            <td>{formatCoord(latitude, true)}, {formatCoord(longitude, false)}</td>
          </tr>
          {timezone && (
            <tr>
              <td>Time Zone</td>
              <td>{timezone}</td>
            </tr>
          )}
          {ascendant && (
            <tr>
              <td>Ascendant</td>
              <td><strong>{ascendant.signName}</strong> ({formatLongitude(ascendant.longitudeDeg)}) • {ascendant.nakshatra} Pāda {ascendant.pada}</td>
            </tr>
          )}
          {moonSign && (
            <tr>
              <td>Moon Sign</td>
              <td><strong>{moonSign.signName}</strong> ({formatLongitude(moonSign.longitudeDeg)}) • {moonSign.nakshatra} Pāda {moonSign.pada}</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* House Cusps */}
      {houses && Object.keys(houses).length > 0 && (
        <>
          <Typography level="body-sm" sx={{ fontWeight: 700, mb: 0.75, mt: 2, color: 'text.primary' }}>
            Houses (Whole Sign System)
          </Typography>
          <Table 
            size="sm" 
            sx={{ 
              '& th': { 
                py: 0.5, 
                fontSize: '0.75rem', 
                fontWeight: 700, 
                textTransform: 'uppercase',
                backgroundColor: 'background.level1',
              },
              '& td': { 
                py: 0.4, 
                fontSize: '0.875rem',
              },
              '& td:first-of-type': { fontWeight: 600, width: '80px' },
            }}
          >
            <thead>
              <tr>
                <th>House</th>
                <th>Sign</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(houseNum => {
                const house = houses[houseNum];
                return (
                  <tr key={houseNum}>
                    <td>H{houseNum}</td>
                    <td>{house?.signName || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}
    </Box>
  );
}
